import { Arrays, OpenAPIV3, ServiceModelSite, SitemapNode, SitemapVisibility, UserSession } from "@salaxy/core";
import angular from "angular";
import { sitemap_PersonLeafs } from "./sitemap_PersonLeafs";
import { sitemap_Company } from "./sitemap_Company";

/**
 * Helper for managing standard web sitemaps in navigation.
 */
export class SitemapHelper {

  public constructor() {
    console.debug("snowpackHack constructor");
  }

  /**
   * Gets logical visibility, as used in the views an UI logic, i.e.:
   *
   * - hidden: Node is null or visibility is Hidden, HiddenReadOnly or Disabled.
   * - readOnly: Node is null or visibility is ReadOnly, HiddenReadOnly.
   * - disabled: Visibility is Disabled.
   * - Visible: Visibility is Visible or null, node is not null.
   * - isFullWidth: The node has an isFullWidth boolean flag set to true.
   * Supports exclamation mark (!) in the beginning to denote negation (true <=> false).
   * @param node Node to evaluate
   * @param evaluateFor The visibility value to evaluate for.
   */
  public static getVisibility(node: SitemapNode, evaluateFor: SitemapVisibility | "isFullWidth" | string): boolean {
    if (evaluateFor?.[0] == "!") {
      return !this.getVisibility(node, evaluateFor.substring(1));
    }
    switch (evaluateFor) {
      case SitemapVisibility.Disabled:
        return node?.visibility == SitemapVisibility.Disabled;
      case SitemapVisibility.Hidden:
        return node == null || node.visibility == SitemapVisibility.Disabled ||
          node.visibility == SitemapVisibility.Hidden || node.visibility == SitemapVisibility.HiddenReadOnly;
      case SitemapVisibility.HiddenReadOnly:
        // Probably does not really make sense, just for copleteness.
        return this.getVisibility(node, SitemapVisibility.Hidden) || this.getVisibility(node, SitemapVisibility.ReadOnly);
      case SitemapVisibility.ReadOnly:
        return node == null || node.visibility == SitemapVisibility.ReadOnly || node.visibility == SitemapVisibility.HiddenReadOnly;
      case SitemapVisibility.Visible:
        return node != null && (!node.visibility || node.visibility == SitemapVisibility.Visible);
      case "isFullWidth":
        return !!node?.isFullWidth;
      default:
        throw new Error("Unknown visibility value to evaluate:" + evaluateFor);
    }
  }

  /**
   * Gets the standard company sitemap for Company web sites.
   */
  public static getCompanySiteMap(): SitemapNode[] {
    return this.setSitemapTitles(sitemap_Company);
  }

  /**
   * Gets the editable version of the sitemap: Full ID's etc.
   * @param site The Site as customized in the Service model.
   * @param nodes Root nodes to start from. If null, a copy of the baseTree is used.
   * @param parentId Identifier of the parent. Used in recursive calls to add fully qualified ID's to all nodes.
   * @returns An editable sitemap.
   */
  public static getEditableSitemap(site: ServiceModelSite, nodes: SitemapNode[] = null, parentId: string = null): SitemapNode[] {
    const i18Prefix = "SALAXY.NG1_Sitemap";
    nodes = nodes || angular.copy(sitemap_Company);
    (nodes || []).forEach((node) => {
      let hasChildrenChanges = false;
      const id = parentId ? `${parentId}.${node.id || "undefined"}` : node.id;
      if (node.children) {
        hasChildrenChanges = !!this.getEditableSitemap(site, node.children, id).find((x => x.$edit.$hasChanges || x.$edit.$hasTextChanges || x.$edit.$hasChildrenChanges));
      }
      const isOpen = !!node.$edit?.$isOpen;
      node.$edit = site.sitemap?.[id] ? angular.copy(site.sitemap?.[id]) : {};
      if (Object.keys(node.$edit).length) {
        node.$edit.$hasChanges = true;
      }
      node.$edit.$hasChildrenChanges = hasChildrenChanges;
      node.$edit.$isOpen = isOpen;
      node.$edit.$id = id;
      const sectionPostFix = node.children ? ".main" : "";
      node.$edit.$i18nKey = `${i18Prefix}.${id}${sectionPostFix}`;
      if (Object.keys(site.texts ?? {}).find((x) => x.endsWith(node.$edit.$i18nKey))) {
        node.$edit.$hasTextChanges = true;
      }
      switch (node.$edit.visibility || node.visibility) {
        case SitemapVisibility.Disabled:
          node.$edit.$faIcons = ["fa-ban"];
          break;
        case SitemapVisibility.Hidden:
          node.$edit.$faIcons = ["fa-eye-slash"];
          break;
        case SitemapVisibility.HiddenReadOnly:
          node.$edit.$faIcons = ["fa-eye-slash", "fa-lock"];
          break;
        case SitemapVisibility.ReadOnly:
          node.$edit.$faIcons = ["fa-lock"];
          break;
        case SitemapVisibility.Visible:
        default:
          node.$edit.$faIcons = [];
          break;
      }
      if (node.isFullWidth || node.$edit.isFullWidth) {
        node.$edit.$faIcons.push("fa-arrows-h");
      }
      node.$edit.visibility = node.$edit.visibility || "default";
      // TODO: Add proper Array editor and remove this (also in commitSitemapNode()).
      node.$edit.roles = Arrays.assureArray(node.$edit.roles).join(",");
      // Direct title is not supported in translations, but it may be used development time.
      node.title = node.title ? node.title : node.$edit.$i18nKey;
    });
    return nodes;
  }

  /**
   * Applies custom properties from service model to set of sitemap nodes.
   * Also does backward compatibility from old SalaxySitemapNode format to current SitemapNode.
   * @param session User session that potentially contains the service model.
   * If null or no partner.serviceModel.sitemap, the method does nothing.
   * @param nodes Nodes that are being updated.
   * @param parentId Identifier of the parent. Used in recursive calls to add fully qualified ID's to all nodes.
   */
  public static setCustomProperties(session: UserSession, nodes: SitemapNode[], parentId: string = null): void {
    if (!nodes?.length) {
      return;
    }
    const sitemapProps = session?.settings?.partner?.serviceModel?.site?.sitemap;
    nodes.forEach((node) => {
      // "company-site" is special root id for the situation when the company site is not the root, e.g. developers.salaxy.com (webs/ng1-dev)
      const id = (parentId && parentId != "company-site") ? `${parentId}.${node.id || "undefined"}` : node.id;

      // Backward compatibility
      if (node.hidden && !node.visibility) {
        node.visibility = SitemapVisibility.Hidden;
      }

      // Set custom properties.
      if (sitemapProps) {
        if (sitemapProps[id]) {
          Object.keys(sitemapProps[id]).forEach((key) => {
            const value = sitemapProps[id][key];
            if (value) {
              node[key] = value;
            }
          });
        }
      }
      this.setCustomProperties(session, node.children, id);
    });
  }

  /** This just to get snowpack working. */
  public snowpackHack(): string {
    return "snowpackHack";
  }

  /**
   * Gets the combined sitemap that contains the structure for both company
   * and personal (Worker, Household). this is used mainly in development and PRO service:
   * typically, company and personal sites are separate and this tree is not used.
   */
   public static getAllRolesSiteMap(): SitemapNode[] {
     const tree = angular.copy(sitemap_Company);
     tree.splice(tree.length - 1, 0, ...sitemap_PersonLeafs);
     return this.setSitemapTitles(tree);
  }

  /**
   * Sets the titles for each node in given sitemap based on node id from the given tranlsation.
   * @param sitemap Selected sitemap to add titles to
   * @param currentPath Current node in sitemap object
   * @param i18Prefix Translation key prefix (defaults to SALAXY.NG1_Sitemap)
   */
  public static setSitemapTitles(sitemap: SitemapNode[], currentPath = "", i18Prefix = "SALAXY.NG1_Sitemap"): SitemapNode[] {
    for (const node of sitemap) {
      const newPath = currentPath ? `${currentPath}.${node.id}` : node.id;
      const sectionPostFix = node.children ? ".main" : "";
      node.title = node.title ? node.title : `${i18Prefix}.${newPath}${sectionPostFix}`;
      if (node.children) {
        this.setSitemapTitles(node.children, newPath, i18Prefix);
      }
    }
    return sitemap;
  }


  /**
   * Removes a section based on id.
   * @param sitemap Sitemap from which the section is removed.
   * @param sectionId Identifier for finding the section to remove.
   */
  public static removeSection(sitemap: SitemapNode[], sectionId: string): void {
    const index = sitemap.findIndex((x) => x.id === sectionId);
    if (index >= 0) {
      sitemap.splice(index, 1);
    }
  }

  /**
   * Gets a section based on id.
   * @param sitemap Sitemap from which the section is fetched.
   * @param sectionId Identifier for finding the section to remove.
   */
  public static getSection(sitemap: SitemapNode[], sectionId: string): SitemapNode {
    return sitemap.find((x) => x.id === sectionId);
  }

  /**
   * Gets a node (subpage) based on id.
   * @param sitemap Sitemap from which the section is fetched.
   * @param sectionId Identifier for finding the section.
   * @param nodeId Identifier for finding the node (subpage to modify or hide).
   */
  public static getNode(sitemap: SitemapNode[], sectionId: string, nodeId: string): SitemapNode {
    const section = this.getSection(sitemap, sectionId);
    if (!section || !section.children) {
      return null;
    }
    return section.children.find((x) => x.id === nodeId);
  }

  /**
   * Gets a JSON schema of a sitemap node using the params property.
   * NOTE: Advanced properties (i18n, children and params) are not included in this schema. Add if needed.
   */
  public static getSchema(node: SitemapNode): OpenAPIV3.SchemaObject {
    const result: OpenAPIV3.SchemaObject = {
      title: "Current sitemap node",
      type: "object",
      properties: {
        id: {
          title: "Identifier for the node: Should be unique within one set of children.",
          type: "string",
        },
        title: {
          title: "Hard-coded single-language title. This should be used in dev/testing only",
          type: "string",
        },
        url: {
          title: "Navigation URL for the node.",
          type: "string",
        },
        roles: {
          title: "Roles for limiting the visibility of the node.",
          anyOf: [
            {
                "type": "string",
            },
            {
              type: "array",
              items: { type: "string" }
            }
          ]
        },
        visibility: {
          title: "Specifies how the node is shown in the navi / UI. Default / null is 'visible'.",
          type: "string",
        },
        isFullWidth: {
          title: "If true, sets container class to container-fluid",
          type: "string",
        },
        /*
        i18n: {
          title: "",
          type: "array",
          items: {
            type: "string",
          }
        },
        children: {
          title: "Array of child nodes.",
          type: "object",
        },
        params: {
          title: "Defines the json schema for parameters that may be defined for a single node/page.",
          type: "object",
        }
        */
      }
    };
    if (node.params) {
      result.properties = {...result.properties, ...node.params};
    }
    return result;
  }
}
